// MorrowOS pricing surface — our own plans, driven by the backend-authoritative
// catalog (GET /api/morrow/billing/plans), NOT Clerk's <PricingTable/>. The CTA
// starts a Stripe Checkout session (POST /api/morrow/billing/checkout) and
// redirects to Stripe; subscribed users get a "Manage billing" portal link.
//
// FNR chrome: 8px cards, hairline borders, sky-blue primary.
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  fetchPlans,
  openBillingPortal,
  startCheckout,
  type BillingPlan,
} from "../../morrowBilling";
import { useEntitlements, startTrial } from "../../morrowEntitlements";
import { toastManager } from "../ui/toast";
import type { Translator } from "~/i18n";

type Audience = "individual" | "business";

export function formatPrice(cents: number, currency: string): string {
  if (cents === 0) return "Free";
  const amount = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency.toUpperCase(),
    maximumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
  return amount;
}

/**
 * Billing-cadence model. Prices are seeded MONTHLY; longer cadences bill upfront
 * at a discount (industry standard): quarterly bills 2.5 months for 3 (½ month /
 * "15 days" free), yearly bills 10 months for 12 ("2 months free"). The per-month
 * headline price is the total ÷ months, so the discount is visible at a glance.
 */
export type BillingInterval = "month" | "quarter" | "year";
export interface BillingIntervalMeta {
  readonly key: BillingInterval;
  readonly label: string;
  readonly months: number;
  /** Human note under the price, e.g. "billed yearly". */
  readonly billedNote: string;
}
export const BILLING_INTERVALS: readonly BillingIntervalMeta[] = [
  { key: "month", label: "Monthly", months: 1, billedNote: "billed monthly" },
  { key: "quarter", label: "Quarterly", months: 3, billedNote: "billed quarterly" },
  { key: "year", label: "Yearly", months: 12, billedNote: "billed yearly" },
];

/**
 * Tiered pre-pay discounts: the higher the plan AND the longer the commitment,
 * the bigger the saving. Free has nothing to discount. Editable in one place so
 * pricing stays consistent everywhere it's shown.
 */
const TIER_DISCOUNTS: Record<string, { quarter: number; year: number }> = {
  pay: { quarter: 10, year: 17 }, // Pro
  super: { quarter: 15, year: 25 }, // Ultra
  max: { quarter: 20, year: 30 }, // Max
};

/** The discount % a given tier gets at a given interval (0 for monthly / free). */
export function tierIntervalDiscount(tier: string, interval: BillingInterval): number {
  if (interval === "month") return 0;
  return TIER_DISCOUNTS[tier]?.[interval] ?? 0;
}

/** The largest discount any tier gets at this interval — for the toggle's "save up to" hint. */
export function maxIntervalDiscount(interval: BillingInterval): number {
  if (interval === "month") return 0;
  return Math.max(0, ...Object.values(TIER_DISCOUNTS).map((d) => d[interval]));
}

export interface IntervalPricing {
  readonly perMonthCents: number;
  readonly totalCents: number;
  readonly discountPct: number;
  readonly meta: BillingIntervalMeta;
}
export function intervalPricing(
  monthlyCents: number,
  interval: BillingInterval,
  tier: string,
): IntervalPricing {
  const meta = BILLING_INTERVALS.find((i) => i.key === interval) ?? BILLING_INTERVALS[0]!;
  const discountPct = tierIntervalDiscount(tier, interval);
  const perMonthCents = Math.round(monthlyCents * (1 - discountPct / 100));
  const totalCents = perMonthCents * meta.months;
  return { perMonthCents, totalCents, discountPct, meta };
}

/** A short "who it's for" tagline per plan tier. */
export function planTagline(tier: string): string {
  switch (tier) {
    case "free":
      return "For very small teams";
    case "pay":
      return "For growing teams";
    case "super":
      return "For scaling businesses";
    case "max":
      return "For large organizations";
    default:
      return "";
  }
}

/** Format a megabyte allowance as MB or GB for plan cards. */
function planStorage(mb: number): string {
  if (mb >= 1024) {
    const gb = mb / 1024;
    return `${gb % 1 === 0 ? gb.toFixed(0) : gb.toFixed(1)} GB`;
  }
  return `${mb} MB`;
}

/**
 * Human-friendly highlight lines for a plan card. Mirrors the Plan & Usage
 * taxonomy: lead with the model access LEVEL, then the real work meter (tool
 * calls — NOT the internal "agent turns"), agents, sub-agent capacity, connected
 * apps, storage, and seats. Coming-soon/unbuilt capabilities are intentionally
 * left off the cards.
 */
export function planHighlights(plan: BillingPlan): string[] {
  const lines: string[] = [];
  const byCap = new Map(plan.entitlements.map((e) => [e.capability, e]));

  // Model access level (one line, not a tier list).
  if (byCap.get("model.tier.max")?.bool) lines.push("All models");
  else if (byCap.get("model.tier.super")?.bool) lines.push("Advanced + standard models");
  else if (byCap.get("model.tier.pay")?.bool) lines.push("Standard models");
  else lines.push("Free models");

  const tools = byCap.get("tool.calls.monthly");
  if (tools) {
    lines.push(
      tools.usageLimit === null
        ? "Unlimited tool calls"
        : `${tools.usageLimit.toLocaleString()} tool calls / mo`,
    );
  }
  const agents = byCap.get("agents.max");
  if (agents) {
    lines.push(
      agents.quantity === null
        ? "Unlimited agents"
        : `${agents.quantity} agent${agents.quantity === 1 ? "" : "s"}`,
    );
  }
  const sub = byCap.get("subagents.concurrent");
  if (sub && sub.rateLimit !== null) lines.push(`${sub.rateLimit} concurrent sub-agents`);

  const connectors = byCap.get("connectors.count");
  if (connectors) {
    lines.push(
      connectors.quantity === null
        ? "Unlimited connected apps"
        : `${connectors.quantity} connected apps`,
    );
  }
  const storage = byCap.get("vault.storage.mb");
  if (storage) {
    lines.push(
      storage.quantity === null
        ? "Unlimited vault storage"
        : `${planStorage(storage.quantity)} vault storage`,
    );
  }
  if (byCap.get("org.memory")?.bool) lines.push("Organization memory");
  if (plan.audience === "business") lines.push(`${plan.seatsIncluded} seats included`);
  return lines;
}

/**
 * The COMPLETE included-feature checklist for a plan (every granted capability,
 * value-labeled), for the detailed plan hero. Ordered models → work → agents →
 * tools → storage → collaboration. Booleans only appear when granted.
 */
export function planFeatureLines(plan: BillingPlan, t?: Translator): string[] {
  const lines: string[] = [];
  const byCap = new Map(plan.entitlements.map((e) => [e.capability, e]));
  const num = (n: number | null) => (n === null ? null : n.toLocaleString());
  const agentLine = (count: number) =>
    t
      ? count === 1
        ? t("pricing.feature.agents", { count })
        : t("pricing.feature.agentsPlural", { count })
      : `${count} agent${count === 1 ? "" : "s"}`;

  if (byCap.get("model.tier.max")?.bool)
    lines.push(t ? t("pricing.feature.allModels") : "Access to all models");
  else if (byCap.get("model.tier.super")?.bool)
    lines.push(t ? t("pricing.feature.advancedStandard") : "Advanced + standard models");
  else if (byCap.get("model.tier.pay")?.bool)
    lines.push(t ? t("pricing.feature.standardModels") : "Standard models");
  else lines.push(t ? t("pricing.feature.freeModels") : "Free models");

  const tools = byCap.get("tool.calls.monthly");
  if (tools)
    lines.push(
      tools.usageLimit === null
        ? t
          ? t("pricing.feature.unlimitedToolCalls")
          : "Unlimited tool calls"
        : t
          ? t("pricing.feature.toolCallsMonth", { count: num(tools.usageLimit)! })
          : `${num(tools.usageLimit)} tool calls / month`,
    );
  const agents = byCap.get("agents.max");
  if (agents)
    lines.push(
      agents.quantity === null
        ? t
          ? t("pricing.feature.unlimitedAgents")
          : "Unlimited agents"
        : agentLine(agents.quantity),
    );
  const sub = byCap.get("subagents.concurrent");
  if (sub && sub.rateLimit !== null)
    lines.push(
      t
        ? t("pricing.feature.concurrentSubagents", { count: sub.rateLimit })
        : `${sub.rateLimit} concurrent sub-agents`,
    );
  if (byCap.get("tools.catalog.full")?.bool)
    lines.push(t ? t("pricing.cap.fullToolLibrary") : "Full tool library (1,100+)");
  const connectors = byCap.get("connectors.count");
  if (connectors)
    lines.push(
      connectors.quantity === null
        ? t
          ? t("pricing.feature.unlimitedConnectors")
          : "Unlimited connected apps"
        : t
          ? t("pricing.feature.connectors", { count: connectors.quantity })
          : `${connectors.quantity} connected apps`,
    );
  const storage = byCap.get("vault.storage.mb");
  if (storage)
    lines.push(
      storage.quantity === null
        ? t
          ? t("pricing.feature.unlimitedVault")
          : "Unlimited vault storage"
        : t
          ? t("pricing.feature.vaultStorage", { size: planStorage(storage.quantity) })
          : `${planStorage(storage.quantity)} vault storage`,
    );
  if (byCap.get("memory.enabled")?.bool)
    lines.push(t ? t("pricing.feature.persistentMemory") : "Persistent memory");
  const mem = byCap.get("memory.storage.mb");
  if (mem && mem.quantity !== null)
    lines.push(
      t
        ? t("pricing.feature.memoryStorage", { size: planStorage(mem.quantity) })
        : `${planStorage(mem.quantity)} memory storage`,
    );
  if (byCap.get("knowledge.base")?.bool)
    lines.push(t ? t("pricing.feature.knowledgeBase") : "Knowledge base");
  if (byCap.get("org.memory")?.bool)
    lines.push(t ? t("pricing.feature.orgMemory") : "Organization memory");
  if (byCap.get("org.roles")?.bool)
    lines.push(t ? t("pricing.feature.rolesPermissions") : "Roles & permissions");
  if (plan.audience === "business")
    lines.push(
      t
        ? t("pricing.feature.memberSeats", { count: plan.seatsIncluded })
        : `${plan.seatsIncluded} member seats`,
    );
  return lines;
}

/**
 * The DELTA of a plan over the previous tier — "Everything in {prev} +" lines.
 * A feature is in the delta if it's newly granted or its numeric allowance grew.
 */
export function planDelta(plan: BillingPlan, prev: BillingPlan | null, t?: Translator): string[] {
  if (!prev) return planFeatureLines(plan, t);
  const prevSet = new Set(planFeatureLines(prev, t));
  return planFeatureLines(plan, t).filter((line) => !prevSet.has(line));
}

/** The model-access level a plan grants — collapses the three model.tier booleans. */
export function planModelLevel(plan: BillingPlan, t?: Translator): string {
  const byCap = new Map(plan.entitlements.map((e) => [e.capability, e]));
  if (byCap.get("model.tier.max")?.bool) return t ? t("pricing.feature.allModels") : "All models";
  if (byCap.get("model.tier.super")?.bool)
    return t ? t("pricing.feature.advancedStandard") : "Advanced + standard";
  if (byCap.get("model.tier.pay")?.bool)
    return t ? t("pricing.feature.standardModels") : "Standard";
  return t ? t("pricing.feature.freeModels") : "Free models";
}

/**
 * One comparison-table cell: what `plan` grants for `capability` of `type`.
 * Returns `true`/`false` for a check/dash, or a formatted string for a value.
 * Storage is in MB; usage/quantity/rate carry their number; unlimited = "Unlimited".
 */
export function planCellValue(
  plan: BillingPlan,
  capability: string,
  type: "boolean" | "quantity" | "usage" | "rate" | "storage",
  t?: Translator,
): boolean | string {
  const unlimited = t ? t("pricing.unlimited") : "Unlimited";
  if (capability === "model.access") return planModelLevel(plan, t);
  const e = plan.entitlements.find((x) => x.capability === capability);
  if (!e) return false; // not in the plan → dash
  if (type === "boolean") return e.bool === true;
  if (type === "storage") {
    if (e.quantity === null) return e.bool === false ? false : unlimited;
    return planStorage(e.quantity);
  }
  const n = e.usageLimit ?? e.quantity ?? e.rateLimit;
  if (n === null || n === undefined) {
    // A present numeric entitlement with no number means unlimited; absent = dash.
    return type === "usage" || type === "quantity" || type === "rate" ? unlimited : false;
  }
  // The AI budget is a $-value stored in MICRO-DOLLARS (µ$, 1e-6 USD) on the plans endpoint
  // (the entitlements summary converts it server-side, the plans list doesn't) — show it as a
  // monthly dollar amount, not a raw 20,000,000.
  if (capability === "ai.value.monthly") {
    return `$${Math.round(n / 1_000_000).toLocaleString()} / mo`;
  }
  const suffix = type === "usage" ? " / mo" : type === "rate" ? " at once" : "";
  return `${n.toLocaleString()}${suffix}`;
}

export function PricingPanel() {
  const entitlements = useEntitlements();
  const [plans, setPlans] = useState<ReadonlyArray<BillingPlan> | null | undefined>(undefined);
  const [audience, setAudience] = useState<Audience>("individual");
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void fetchPlans().then((result) => {
      if (!cancelled) setPlans(result);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const visiblePlans = useMemo(
    () =>
      (plans ?? [])
        .filter((p) => p.audience === audience)
        .slice()
        .sort((a, b) => a.rank - b.rank),
    [plans, audience],
  );

  const onSelect = useCallback(async (plan: BillingPlan) => {
    setBusySlug(plan.slug);
    // Individual plans start the 7-day NO-CREDIT-CARD reverse trial (startTrial →
    // /api/morrow/billing/start-trial), never Stripe Checkout — Checkout collects a
    // card and, for the price-less trial plan, 400s so the button looked dead. Only
    // when the caller isn't eligible for the free trial (already trialed, or already
    // on a plan) do we fall through to Checkout so they can actually pay.
    if (plan.audience === "individual") {
      // Cardless trial of THIS plan's real entitlements. "trialing" → unlocked in place;
      // "error" → transient failure, let them retry. Neither asks for a card. Only a definitive
      // "ineligible" (trial already spent, or already on a plan) falls through to Checkout.
      const result = await startTrial(plan.slug);
      if (result.outcome !== "ineligible") {
        if (result.outcome === "error") {
          toastManager.add({
            type: "error",
            title: "Couldn't start the free trial",
            description: result.message ?? "Please try again.",
          });
        }
        setBusySlug(null);
        return;
      }
    }
    const url = await startCheckout(plan.slug);
    if (url) {
      window.location.href = url;
      return;
    }
    toastManager.add({
      type: "error",
      title: "Couldn't start checkout",
      description:
        "This plan can't be started from here right now. Please try again or contact support.",
    });
    setBusySlug(null);
  }, []);

  const onManageBilling = useCallback(async () => {
    setPortalBusy(true);
    const url = await openBillingPortal();
    if (url) {
      window.location.href = url;
      return;
    }
    setPortalBusy(false);
  }, []);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-6 text-foreground">
      <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-3">
        <div>
          <h1 className="text-base font-semibold tracking-tight text-foreground">
            Plans & Pricing
          </h1>
          <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
            Choose the tier that fits your workflow
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AudienceToggle audience={audience} onChange={setAudience} />
          <button
            type="button"
            onClick={() => void onManageBilling()}
            disabled={portalBusy}
            className="rounded-lg border border-border bg-muted px-3 py-1.5 text-xs font-semibold text-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {portalBusy ? "Opening…" : "Manage billing"}
          </button>
        </div>
      </header>

      {plans === undefined ? (
        <Hint>Loading plans…</Hint>
      ) : plans === null ? (
        <Hint>Plans are unavailable right now. Try refreshing in a moment.</Hint>
      ) : visiblePlans.length === 0 ? (
        <Hint>No plans are published for this audience yet.</Hint>
      ) : (
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {visiblePlans.map((plan) => {
            const isCurrent =
              entitlements.isLoaded &&
              entitlements.tier === plan.tier &&
              entitlements.planName === plan.name;
            const isFree = plan.priceCents === 0;
            return (
              <article
                key={plan.slug}
                className={
                  "card-glass flex flex-col gap-3 rounded-lg border p-4 " +
                  (isCurrent ? "border-primary" : "border-transparent")
                }
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-foreground">{plan.name}</h2>
                  {isCurrent ? (
                    <span className="rounded-md border border-primary px-1.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                      Current
                    </span>
                  ) : null}
                </div>
                <div className="text-2xl font-semibold tabular-nums">
                  {formatPrice(plan.priceCents, plan.currency)}
                  {!isFree ? (
                    <span className="text-xs font-medium text-muted-foreground">
                      {" "}
                      / {plan.interval}
                    </span>
                  ) : null}
                </div>
                <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
                  {planHighlights(plan).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <div className="mt-auto pt-2">
                  {isCurrent ? (
                    <button
                      type="button"
                      disabled
                      className="w-full rounded-lg border border-border bg-muted px-3 py-1.5 text-xs font-semibold text-muted-foreground"
                    >
                      Current plan
                    </button>
                  ) : isFree ? (
                    <button
                      type="button"
                      disabled
                      className="w-full rounded-lg border border-border bg-muted px-3 py-1.5 text-xs font-semibold text-muted-foreground"
                    >
                      Included
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void onSelect(plan)}
                      disabled={busySlug !== null}
                      className="w-full rounded-lg border border-border bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
                    >
                      {busySlug === plan.slug ? "Starting…" : "Start free trial"}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </section>
      )}
    </div>
  );
}

function AudienceToggle({
  audience,
  onChange,
}: {
  readonly audience: Audience;
  readonly onChange: (audience: Audience) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-muted p-0.5 text-xs font-semibold">
      {(["individual", "business"] as const).map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onChange(option)}
          className={
            "rounded-md px-2.5 py-1 capitalize transition-colors " +
            (audience === option
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground")
          }
        >
          {option}
        </button>
      ))}
    </div>
  );
}

function Hint({ children }: { readonly children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border bg-muted p-4 text-sm font-medium text-foreground">
      {children}
    </section>
  );
}
